/**
 * Timeline Bar Component
 *
 * Displays timeline controls in the context bar: tool buttons on the left
 * (loop, speed, reset), tick-based timeline axis with draggable needle in
 * the center, play/pause on the right. Time label opens a popover with
 * current time, start/end datetime editors.
 */

import { LitElement, html, css } from 'lit'
import { customElement, property, state, query } from 'lit/decorators.js'
import { type TimelineState, PLAYBACK_LABELS } from '../../services/timeline/timeline-service.js'
import {
  formatTime,
  formatDateShort,
  toDatetimeLocalValue,
  generateTicks,
  getNeedleFraction,
  type Tick,
} from '../../lib/timeline-utils.js'
import '@material/web/button/text-button.js'

@customElement('timeline-bar')
export class TimelineBar extends LitElement {
  static override styles = css`
    :host {
      display: flex;
      align-items: stretch;
      width: 100%;
      height: 100%;
      font-size: 13px;
      font-weight: 500;
      color: white;
      position: relative;
    }

    .material-icons {
      font-family: 'Material Symbols Outlined';
      font-size: 20px;
    }

    /* --- Time label --- */

    .time-label {
      /* Reset button UA styles */
      background: none;
      border: none;
      color: inherit;
      font: inherit;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      white-space: nowrap;
      min-width: 56px;
      line-height: 1.2;
      cursor: pointer;
      padding: 4px 8px;
      border-radius: 6px;
      transition: background 0.15s ease;
      flex-shrink: 0;
    }

    .time-label:hover {
      background: rgba(255, 255, 255, 0.12);
    }

    .time-label-date {
      font-size: 10px;
      opacity: 0.7;
    }

    .time-label-time {
      font-size: 13px;
      font-weight: 600;
    }

    /* --- Timeline axis --- */

    .timeline-axis {
      flex: 1;
      min-width: 0;
      height: 100%;
      position: relative;
      cursor: pointer;
      overflow: visible;
      touch-action: none;
    }

    .axis-track {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 1px;
      height: 2px;
      background: white;
    }

    .tick {
      position: absolute;
      bottom: 1px;
      width: 2px;
      background: white;
    }

    .tick.minor {
      height: 8px;
    }

    .tick.major {
      height: 16px;
    }

    .tick-label {
      position: absolute;
      bottom: 20px;
      transform: translateX(-50%);
      font-size: 11px;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.75);
      white-space: nowrap;
      pointer-events: none;
      user-select: none;
    }

    .needle {
      position: absolute;
      top: 2px;
      bottom: -2px;
      width: 3px;
      background: #5a7088;
      transform: translateX(-1.5px);
      pointer-events: none;
      z-index: 1;
      box-shadow:
        -1px 0 0 rgba(255, 255, 255, 0.8),
        1px 0 0 rgba(255, 255, 255, 0.8);
    }

    .needle::after {
      content: '';
      position: absolute;
      top: 0;
      left: 50%;
      transform: translateX(-50%);
      width: 10px;
      height: 10px;
      background: #5a7088;
      border-radius: 50%;
      box-shadow:
        0 0 0 2px rgba(255, 255, 255, 0.8),
        0 0 6px rgba(90, 112, 136, 0.6);
    }

    .timeline-axis:hover {
      cursor: grab;
    }

    .timeline-axis.dragging {
      cursor: grabbing;
    }

    /* --- Play button (right side) --- */

    .play-button {
      width: 48px;
      height: 100%;
      background: none;
      border: none;
      color: rgba(255, 255, 255, 0.85);
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.15s ease;
      padding: 0;
      flex-shrink: 0;
    }

    .play-button:hover {
      color: white;
      background: rgba(255, 255, 255, 0.12);
    }

    .play-button .material-icons {
      font-size: 24px;
    }

    /* --- Popover ---
     *
     * Uses the HTML Popover API (popover=auto) so the element is rendered
     * in the browser top layer, escaping all ancestor clipping and stacking
     * contexts. Required here because cairn-context-bar host has
     * backdrop-filter: blur, which creates a containing block for
     * fixed-positioned descendants AND combines with overflow: hidden to
     * clip anything inside the bar — so neither position: absolute nor
     * position: fixed would work.
     */

    .time-popover {
      /* Override the UA default [popover] styles (centered with auto inset) */
      position: fixed;
      inset: auto;
      margin: 0;
      border: none;
      background: var(--cairn-surface-color, #ffffff);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-radius: 12px;
      padding: 16px;
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
      min-width: 280px;
      color: var(--cairn-text-color, #1e293b);
    }

    .time-popover:popover-open {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .time-field {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .time-field label {
      font-size: 11px;
      color: var(--cairn-text-secondary-color, #64748b);
    }

    .time-field input {
      width: 100%;
      box-sizing: border-box;
      padding: 10px 12px;
      border: 1px solid var(--cairn-border-color, #e2e8f0);
      border-radius: 8px;
      background: transparent;
      color: var(--cairn-text-color, #1e293b);
      font-size: 14px;
      font-family: inherit;
      /* Let the native datetime-local widget (calendar icon, spinners) follow the theme
         instead of being locked to dark — otherwise its icon is white/invisible in light mode. */
      color-scheme: light dark;
      outline: none;
      transition: border-color 0.15s ease;
    }

    .time-field input:hover {
      border-color: var(--cairn-text-secondary-color, #64748b);
    }

    .time-field input:focus {
      border-color: var(--cairn-primary-color, #1976d2);
    }

    .popover-toolbar {
      display: flex;
      gap: 4px;
      margin-bottom: 4px;
    }

    .popover-tool-button {
      flex: 1;
      /* background-color/border-color/text-secondary are re-themed by applyTheme on manual
         theme switch; background-color-alt is NOT (it only follows the OS @media), so it must
         be avoided here or the buttons render dark in a manually-selected light theme. */
      background: var(--cairn-background-color, #f1f5f9);
      border: none;
      color: var(--cairn-text-secondary-color, #64748b);
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      padding: 8px 4px;
      border-radius: 8px;
      transition: all 0.15s ease;
    }

    .popover-tool-button:hover {
      background: var(--cairn-border-color, #e2e8f0);
      color: var(--cairn-text-color, #1e293b);
    }

    .popover-tool-button.active {
      background: var(--cairn-primary-color, #1976d2);
      color: #ffffff;
    }

    .popover-tool-button .material-icons {
      font-size: 20px;
    }

    .popover-tool-label {
      font-size: 9px;
      font-weight: 500;
      line-height: 1;
    }

    .popover-actions {
      display: flex;
      gap: 8px;
      justify-content: flex-end;
    }

    .popover-actions md-text-button {
      --md-text-button-label-text-color: var(--cairn-text-secondary-color, #64748b);
      --md-text-button-hover-label-text-color: var(--cairn-text-color, #1e293b);
      --md-text-button-hover-state-layer-color: var(--cairn-primary-color, #1976d2);
    }
  `

  @property({ type: Object })
  timelineState?: TimelineState

  @state()
  private isDraggingNeedle = false

  private popoverOpen = false

  @query('.timeline-axis')
  private axisEl?: HTMLElement

  @query('.time-label')
  private timeLabelEl?: HTMLElement

  @query('.time-popover')
  private popoverEl?: HTMLElement

  private popoverId = `time-popover-${Math.random().toString(36).slice(2, 10)}`

  private resizeObserver: ResizeObserver | undefined

  @state()
  private axisWidth = 200

  override firstUpdated(): void {
    if (this.axisEl && this.resizeObserver) {
      this.resizeObserver.observe(this.axisEl)
      this.axisWidth = this.axisEl.clientWidth
    }
  }

  private closePopover(): void {
    if (this.popoverEl && this.popoverOpen) {
      this.popoverEl.hidePopover()
      this.popoverOpen = false
    }
  }

  private emitEvent(name: string, detail?: unknown): void {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
      })
    )
  }

  // --- Needle dragging ---

  private fractionFromPointerEvent(e: PointerEvent): number {
    const rect = this.axisEl!.getBoundingClientRect()
    const x = e.clientX - rect.left
    return Math.max(0, Math.min(1, x / rect.width))
  }

  private scrubToFraction(fraction: number): void {
    if (!this.timelineState) {
      return
    }
    const { startTime, endTime } = this.timelineState
    const range = endTime.getTime() - startTime.getTime()
    const time = new Date(startTime.getTime() + range * fraction)
    this.emitEvent('timeline-scrub', { time })
  }

  private handleAxisPointerDown(e: PointerEvent): void {
    e.preventDefault()
    this.isDraggingNeedle = true
    this.axisEl!.setPointerCapture(e.pointerId)
    this.scrubToFraction(this.fractionFromPointerEvent(e))
  }

  private handleAxisPointerMove(e: PointerEvent): void {
    if (!this.isDraggingNeedle) {
      return
    }
    this.scrubToFraction(this.fractionFromPointerEvent(e))
  }

  private handleAxisPointerUp(e: PointerEvent): void {
    if (!this.isDraggingNeedle) {
      return
    }
    this.isDraggingNeedle = false
    this.axisEl!.releasePointerCapture(e.pointerId)
  }

  // --- Control handlers ---

  private handlePlayPause(): void {
    if (!this.timelineState) {
      return
    }
    this.emitEvent(this.timelineState.isPlaying ? 'timeline-pause' : 'timeline-play')
  }

  private handleLoop(): void {
    this.emitEvent('timeline-loop-toggled')
  }

  private handleSpeedCycle(): void {
    this.emitEvent('timeline-speed-changed')
  }

  private handleTimeLabelClick(): void {
    if (!this.popoverEl || !this.timeLabelEl) return
    if (this.popoverOpen) {
      this.closePopover()
      return
    }
    const rect = this.timeLabelEl.getBoundingClientRect()
    this.popoverEl.style.top = `${rect.bottom + 8}px`
    this.popoverEl.style.left = `${rect.left}px`
    this.popoverEl.showPopover()
    this.popoverOpen = true

    if (this.timelineState) {
      const inputs = this.popoverEl.querySelectorAll<HTMLInputElement>(
        'input[type="datetime-local"]'
      )
      const { currentTime, startTime, endTime } = this.timelineState
      if (inputs[0]) inputs[0].value = toDatetimeLocalValue(currentTime)
      if (inputs[1]) inputs[1].value = toDatetimeLocalValue(startTime)
      if (inputs[2]) inputs[2].value = toDatetimeLocalValue(endTime)
    }
  }

  private handlePopoverClickOutside = (e: MouseEvent): void => {
    if (!this.popoverOpen || !this.popoverEl) return
    const path = e.composedPath()
    if (!path.includes(this.popoverEl) && !path.includes(this.timeLabelEl!)) {
      this.closePopover()
    }
  }

  private handleInputClick(e: Event): void {
    // Firefox doesn't natively focus inputs inside shadow DOM popovers
    // promoted to the top layer — explicit focus() works around this.
    ;(e.target as HTMLInputElement).focus()
  }

  override connectedCallback(): void {
    super.connectedCallback()
    this.resizeObserver = new ResizeObserver((): void => {
      const el = this.renderRoot.querySelector('.timeline-axis')
      if (el) {
        this.axisWidth = el.clientWidth
      }
    })
    document.addEventListener('click', this.handlePopoverClickOutside, true)
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback()
    this.resizeObserver?.disconnect()
    document.removeEventListener('click', this.handlePopoverClickOutside, true)
  }

  private handleStartChanged(e: Event): void {
    const input = e.target as HTMLInputElement
    if (!input.value || !this.timelineState) {
      return
    }
    const start = new Date(input.value)
    if (isNaN(start.getTime()) || start >= this.timelineState.endTime) {
      return
    }
    this.emitEvent('timeline-range-changed', { start, end: this.timelineState.endTime })
  }

  private handleEndChanged(e: Event): void {
    const input = e.target as HTMLInputElement
    if (!input.value || !this.timelineState) {
      return
    }
    const end = new Date(input.value)
    if (isNaN(end.getTime()) || end <= this.timelineState.startTime) {
      return
    }
    this.emitEvent('timeline-range-changed', { start: this.timelineState.startTime, end })
  }

  private handleCurrentTimeChanged(e: Event): void {
    const input = e.target as HTMLInputElement
    if (!input.value || !this.timelineState) {
      return
    }
    const time = new Date(input.value)
    if (
      isNaN(time.getTime()) ||
      time < this.timelineState.startTime ||
      time > this.timelineState.endTime
    ) {
      return
    }
    this.emitEvent('timeline-scrub', { time })
  }

  private handleReset(): void {
    this.emitEvent('timeline-reset')
    this.closePopover()
  }

  // --- Tick generation (delegates to timeline-utils) ---

  private generateTickData(): Tick[] {
    if (!this.timelineState) {
      return []
    }
    return generateTicks(this.timelineState.startTime, this.timelineState.endTime, this.axisWidth)
  }

  private getNeedleFractionValue(): number {
    if (!this.timelineState) {
      return 0
    }
    const { currentTime, startTime, endTime } = this.timelineState
    return getNeedleFraction(currentTime, startTime, endTime)
  }

  override render(): unknown {
    if (!this.timelineState) {
      return html`<span class="time-label"
        ><span class="time-label-time" style="opacity: 0.5">No timeline</span></span
      >`
    }

    const { currentTime, isPlaying, isLooping, playbackDuration } = this.timelineState
    const ticks = this.generateTickData()
    const needlePct = this.getNeedleFractionValue() * 100

    return html`
      <button
        class="time-label"
        type="button"
        @click=${this.handleTimeLabelClick}
        title="Set time range"
      >
        <span class="time-label-date">${formatDateShort(currentTime)}</span>
        <span class="time-label-time">${formatTime(currentTime)}</span>
      </button>

      <div class="time-popover" id=${this.popoverId} popover="manual">
        <div class="popover-toolbar">
          <button
            class="popover-tool-button ${isLooping ? 'active' : ''}"
            @click=${this.handleLoop}
            title="Loop"
          >
            <span class="material-icons">repeat</span>
            <span class="popover-tool-label">Loop</span>
          </button>

          <button
            class="popover-tool-button"
            @click=${this.handleSpeedCycle}
            title="Playback duration"
          >
            <span class="material-icons">speed</span>
            <span class="popover-tool-label">${PLAYBACK_LABELS[playbackDuration]}</span>
          </button>

          <button class="popover-tool-button" @click=${this.handleReset} title="Reset to start">
            <span class="material-icons">restart_alt</span>
            <span class="popover-tool-label">Reset</span>
          </button>
        </div>

        <div class="time-field">
          <label>Current time</label>
          <input
            type="datetime-local"
            step="60"
            @click=${this.handleInputClick}
            @change=${this.handleCurrentTimeChanged}
          />
        </div>
        <div class="time-field">
          <label>Start</label>
          <input
            type="datetime-local"
            step="60"
            @click=${this.handleInputClick}
            @change=${this.handleStartChanged}
          />
        </div>
        <div class="time-field">
          <label>End</label>
          <input
            type="datetime-local"
            step="60"
            @click=${this.handleInputClick}
            @change=${this.handleEndChanged}
          />
        </div>
        <div class="popover-actions">
          <md-text-button @click=${this.closePopover}>Done</md-text-button>
        </div>
      </div>

      <div
        class="timeline-axis ${this.isDraggingNeedle ? 'dragging' : ''}"
        @pointerdown=${this.handleAxisPointerDown}
        @pointermove=${this.handleAxisPointerMove}
        @pointerup=${this.handleAxisPointerUp}
        @pointercancel=${this.handleAxisPointerUp}
      >
        <div class="axis-track"></div>
        ${ticks.map(
          tick => html`
            <div
              class="tick ${tick.major ? 'major' : 'minor'}"
              style="left: ${tick.fraction * 100}%"
            ></div>
            ${tick.label
              ? html`
                  <span class="tick-label" style="left: ${tick.fraction * 100}%"
                    >${tick.label}</span
                  >
                `
              : ''}
          `
        )}
        <div class="needle" style="left: ${needlePct}%"></div>
      </div>

      <button
        class="play-button"
        @click=${this.handlePlayPause}
        title=${isPlaying ? 'Pause' : 'Play'}
      >
        <span class="material-icons">${isPlaying ? 'pause' : 'play_arrow'}</span>
      </button>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'timeline-bar': TimelineBar
  }
}
